import React from 'react';
import { Sun, Battery, Wrench, Building, Home, Shield, CheckCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Services = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Solar Panel Installation and Battery Storage",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Energy Spectrum",
      "telephone": "0404844257",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Leppington",
        "addressRegion": "NSW",
        "postalCode": "2179",
        "addressCountry": "AU"
      }
    },
    "areaServed": {
      "@type": "State",
      "name": "New South Wales"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Solar and Battery Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Residential Solar Installation Sydney"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Commercial Solar Installation Sydney"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Battery Storage Installation Sydney"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Solar Panel Cleaning and Maintenance Sydney"
          }
        }
      ]
    }
  };

  const mainServices = [
    {
      icon: Home,
      title: 'Residential Solar Systems',
      description: 'Complete solar solutions for your home, from consultation to installation and beyond.',
      image: 'https://images.unsplash.com/photo-1756542713155-94f62d47d1b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxyZXNpZGVudGlhbCUyMHNvbGFyJTIwcGFuZWxzJTIwcm9vZnRvcHxlbnwwfHx8fDE3NjAyMjg1MDV8MA&ixlib=rb-4.1.0&q=85',
      features: [
        'Free site assessment and energy audit',
        'Custom system design for your home',
        'Premium solar panels with 25-year warranty',
        'Professional installation by certified technicians',
        'Grid connection and permit handling',
        'Ongoing monitoring and support'
      ]
    },
    {
      icon: Building,
      title: 'Commercial Solar Solutions',
      description: 'Scalable solar systems designed to reduce operational costs and enhance your business sustainability.',
      image: 'https://images.unsplash.com/photo-1726866492047-7f9516558c6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxjb21tZXJjaWFsJTIwc29sYXIlMjBpbnN0YWxsYXRpb258ZW58MHx8fHwxNzYwMjI4NTEwfDA&ixlib=rb-4.1.0&q=85',
      features: [
        'Comprehensive energy analysis',
        'ROI calculations and financial modeling',
        'Large-scale system design and engineering',
        'Minimal disruption to business operations',
        'Commercial-grade equipment and warranties',
        'Maintenance contracts and fleet management'
      ]
    },
    {
      icon: Battery,
      title: 'Battery Storage Systems',
      description: 'Store your solar energy for use anytime, maximizing your energy independence.',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxzb2xhciUyMHBhbmVsc3xlbnwwfHx8fDE3NjAyMDIzNjV8MA&ixlib=rb-4.1.0&q=85',
      features: [
        'Tesla Powerwall and premium battery options',
        'Integration with existing solar systems',
        'Backup power during outages',
        'Time-of-use optimization',
        'Smart energy management',
        '10-year battery warranty'
      ]
    },
    {
      icon: Wrench,
      title: 'Solar Maintenance & Cleaning',
      description: 'Keep your system performing at peak efficiency with our professional maintenance services.',
      image: 'https://images.unsplash.com/photo-1660330589505-9a433a742a7b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHw0fHxzb2xhciUyMHBhbmVsJTIwbWFpbnRlbmFuY2V8ZW58MHx8fHwxNzYwNjY3MTI1fDA&ixlib=rb-4.1.0&q=85',
      features: [
        'Professional panel cleaning',
        'System performance inspection',
        'Inverter and connection checks',
        'Bird proofing and pest prevention',
        'Annual maintenance packages',
        'Emergency repair services'
      ]
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Consultation',
      description: 'We discuss your energy needs and assess your property for solar potential'
    },
    {
      step: '02',
      title: 'Design',
      description: 'Our engineers create a custom solar solution optimized for your situation'
    },
    {
      step: '03',
      title: 'Installation',
      description: 'Certified technicians install your system with minimal disruption'
    },
    {
      step: '04',
      title: 'Activation',
      description: 'We handle all paperwork, permits, and grid connection for you'
    }
  ];

  return (
    <>
      <SEO 
        title="Solar Installation Sydney | Battery Storage & Commercial Solar NSW"
        description="Professional solar installation Sydney. Residential & commercial solar systems, battery storage, solar maintenance. Sigenergy, Fox ESS, Goodwe. Best solar installer Sydney."
        keywords="solar installation Sydney, best solar installer Sydney, solar power systems Sydney, solar battery installation Sydney, commercial solar installation Sydney, solar maintenance Sydney"
        schemaMarkup={serviceSchema}
      />
      <div className="services-container">
        {/* Hero Section */}
        <section className="page-hero">
        <div className="container">
          <h1 className="display-medium">Our Services</h1>
          <p className="body-large page-hero-description">
            Comprehensive solar energy solutions for homes and businesses across NSW
          </p>
        </div>
      </section>

      {/* Main Services */}
      <section className="main-services-section">
        <div className="container">
          {mainServices.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div 
                key={index} 
                className={`service-detail ${index % 2 === 1 ? 'reverse' : ''}`}
              >
                <div className="service-detail-image">
                  <img src={service.image} alt={`${service.title} - Professional installation by Energy Spectrum Sydney`} />
                </div>
                <div className="service-detail-content">
                  <div className="service-detail-icon">
                    <IconComponent size={40} />
                  </div>
                  <h2 className="heading-2">{service.title}</h2>
                  <p className="body-medium service-detail-description">{service.description}</p>
                  <ul className="service-features">
                    {service.features.map((feature, fIndex) => (
                      <li key={fIndex} className="service-feature-item">
                        <CheckCircle size={20} className="feature-check" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Our Process</h2>
            <p className="body-large section-description">
              From consultation to activation, we make going solar simple
            </p>
          </div>
          <div className="process-grid">
            {process.map((item, index) => (
              <div key={index} className="process-card">
                <div className="process-step">{item.step}</div>
                <h3 className="heading-3">{item.title}</h3>
                <p className="body-medium">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="services-cta-section">
        <div className="container">
          <Card className="cta-card">
            <div className="cta-card-content">
              <h2 className="heading-1">Ready to Start Your Solar Journey?</h2>
              <p className="body-large">
                Get a free, no-obligation quote today and discover how much you can save
              </p>
              <Link to="/contact">
                <Button className="btn-cta">Request Free Quote</Button>
              </Link>
            </div>
          </Card>
        </div>
      </section>
      </div>
    </>
  );
};

export default Services;